// Docs page — ported from app/frontend/src/pages/Docs.jsx into the v2
// idiom: MissionControlHero, .card primitives, design tokens, and a
// sticky anchored TOC for jump-to-section. Available to all visitors
// (pre- and post-org-connect) so a brand-new SE can read "Quick start"
// without first connecting.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MissionControlHero } from "@/components/hero/MissionControlHero";

interface Section {
  id: string;
  title: string;
  // Free-text body (paragraphs / lists / tables) — passed as React
  // children so each section can have its own structured layout
  // without inventing a markdown layer.
  render: () => JSX.Element;
}

export function Docs(): JSX.Element {
  useEffect(() => {
    document.title = "Docs · Generator";
  }, []);

  const sections = useMemo<Section[]>(
    () => [
      { id: "quick-start", title: "Quick start templates", render: QuickStart },
      { id: "user-flow", title: "User flow", render: UserFlow },
      { id: "shortcuts", title: "Keyboard shortcuts", render: Shortcuts },
      { id: "custom-pack", title: "Creating a custom industry pack", render: CustomPack },
      { id: "import-json", title: "Importing a JSON pack", render: ImportJson },
      { id: "stories", title: "Demo stories", render: DemoStories },
      { id: "data-cloud", title: "Data 360 & Tableau Next", render: DataCloud },
      { id: "after-build", title: "After provisioning", render: AfterBuild },
      { id: "features", title: "Features", render: Features },
      { id: "reviews", title: "Community pack reviews", render: Reviews },
      { id: "webhooks", title: "Webhooks (admin)", render: Webhooks },
      { id: "curator", title: "Curator", render: Curator },
      { id: "how", title: "How it works", render: HowItWorks },
      { id: "volumes", title: "Volume presets", render: Volumes },
      { id: "explorer", title: "Data Explorer", render: Explorer },
      { id: "troubleshooting", title: "Troubleshooting", render: Troubleshooting },
      { id: "api", title: "API reference", render: ApiReference },
    ],
    [],
  );

  // Filter sections by free-text query (title only — section bodies are
  // React, not strings, so we keep the search lightweight). The query
  // also drives an in-page highlight via the `[data-match]` selector.
  const [q, setQ] = useState("");
  const matched = useMemo(() => {
    if (!q.trim()) return sections;
    const needle = q.toLowerCase();
    return sections.filter((s) => s.title.toLowerCase().includes(needle));
  }, [q, sections]);

  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "28px 24px 120px" }}>
      <MissionControlHero
        breadcrumb="Help"
        title="Documentation"
        subtitle="How Generator works, what it ships, and where to look when something breaks."
        iconGlyph="?"
        astro={{ pose: "happy", message: "Pick a section ↘" }}
        chips={[
          { label: `${sections.length} sections`, bg: "rgba(255,255,255,.14)" },
          { label: "Always available", bg: "rgba(255,255,255,.14)" },
        ]}
        rightSlot={
          <a
            href="/api-redoc"
            target="_blank"
            rel="noreferrer"
            className="btn hero-ghost lg"
            style={{ whiteSpace: "nowrap", textDecoration: "none" }}
          >
            ↗ Interactive API
          </a>
        }
      />

      <div
        style={{
          marginTop: 20,
          display: "grid",
          gridTemplateColumns: "220px 1fr",
          gap: 24,
          alignItems: "start",
        }}
      >
        {/* Sticky TOC */}
        <nav
          aria-label="On this page"
          style={{
            position: "sticky",
            top: 68,
            alignSelf: "start",
            maxHeight: "calc(100vh - 96px)",
            overflowY: "auto",
            paddingRight: 4,
          }}
          className="thin-scroll"
        >
          <div className="label" style={{ marginBottom: 10 }}>
            On this page
          </div>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter sections…"
            style={{
              width: "100%",
              padding: "6px 10px",
              fontSize: 12,
              borderRadius: 6,
              border: "1px solid var(--bd)",
              background: "white",
              color: "var(--ink)",
              marginBottom: 10,
            }}
          />
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {matched.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  style={{
                    display: "block",
                    fontSize: 12,
                    color: "var(--ink-2)",
                    textDecoration: "none",
                    padding: "5px 8px",
                    borderRadius: 6,
                    lineHeight: 1.3,
                  }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLAnchorElement).style.background =
                      "var(--cb-95)")
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLAnchorElement).style.background =
                      "transparent")
                  }
                >
                  {s.title}
                </a>
              </li>
            ))}
            {matched.length === 0 && (
              <li
                style={{
                  fontSize: 11,
                  color: "var(--ink-3)",
                  padding: "5px 8px",
                }}
              >
                No sections match.
              </li>
            )}
          </ul>
        </nav>

        {/* Sections */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {matched.map((s) => (
            <section
              key={s.id}
              id={s.id}
              className="card"
              style={{
                padding: "20px 22px",
                scrollMarginTop: 68,
              }}
            >
              <h2
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "var(--ink)",
                  margin: "0 0 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {s.title}
                <a
                  href={`#${s.id}`}
                  aria-label="Link to section"
                  style={{
                    fontSize: 11,
                    color: "var(--ink-3)",
                    textDecoration: "none",
                    fontWeight: 500,
                  }}
                  title="Copy link to this section"
                >
                  #
                </a>
              </h2>
              {s.render()}
            </section>
          ))}
          <NeedHelp />
        </div>
      </div>
    </div>
  );
}

// ─── Section bodies ────────────────────────────────────────────────

const P_STYLE: React.CSSProperties = {
  fontSize: 13,
  color: "var(--ink-2)",
  lineHeight: 1.6,
  margin: "0 0 8px",
};
const UL_STYLE: React.CSSProperties = {
  margin: "8px 0 0 18px",
  padding: 0,
  fontSize: 13,
  color: "var(--ink-2)",
  lineHeight: 1.6,
  display: "flex",
  flexDirection: "column",
  gap: 4,
};
const OL_STYLE: React.CSSProperties = { ...UL_STYLE };

function Code({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <code
      style={{
        background: "var(--cb-95)",
        color: "var(--ink)",
        fontSize: 11,
        padding: "1px 6px",
        borderRadius: 4,
        fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
      }}
    >
      {children}
    </code>
  );
}

function Strong({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <strong style={{ color: "var(--ink)", fontWeight: 600 }}>{children}</strong>
  );
}

function TableShell({
  headers,
  rows,
}: {
  headers: string[];
  rows: React.ReactNode[][];
}): JSX.Element {
  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          fontSize: 12,
          borderCollapse: "collapse",
        }}
      >
        <thead>
          <tr>
            {headers.map((h) => (
              <th
                key={h}
                style={{
                  textAlign: "left",
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: ".06em",
                  color: "var(--ink-3)",
                  fontWeight: 700,
                  padding: "8px 10px",
                  borderBottom: "1px solid var(--bd)",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: "8px 10px",
                    borderBottom: "1px solid var(--bd-2)",
                    color: "var(--ink-2)",
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QuickStart(): JSX.Element {
  return (
    <>
      <p style={P_STYLE}>
        The Configure page offers one-click presets in the top-left panel.
        Pick one to auto-fill all settings.
      </p>
      <TableShell
        headers={["Template", "Pack", "Volume", "~Time"]}
        rows={[
          ["⚡ Quick Demo", "NTO", "Tiny · Sales + Service", "~10 min"],
          ["📊 Sales Focus", "CRE", "Small · Sales only", "~15 min"],
          ["🎧 Service Focus", "CRE", "Small · Service only", "~15 min"],
          ["👔 Full Demo", "NTO", "Medium · Sales + Service", "~25 min"],
          ["☁️ Data 360 Setup", "Any", "DC + TN only, no data", "~8 min"],
        ]}
      />
    </>
  );
}

function UserFlow(): JSX.Element {
  return (
    <ol style={OL_STYLE}>
      <li>
        <Strong>Connect</Strong> — Click &quot;Connect your org&quot; and complete OAuth
      </li>
      <li>
        <Strong>Configure</Strong> — Pick an industry pack, volume, and demo stories
      </li>
      <li>
        <Strong>Launch</Strong> — Click &quot;Review &amp; launch&quot; to start provisioning
      </li>
      <li>
        <Strong>Monitor</Strong> — Watch progress; when complete, open your org
      </li>
      <li>
        <Strong>Refresh dates</Strong> — Use &quot;Refresh demo dates&quot; to keep dashboards current
      </li>
    </ol>
  );
}

function Shortcuts(): JSX.Element {
  const items: Array<[string, string]> = [
    ["⌘K / Ctrl K", "Open command palette (jump anywhere)"],
    ["g  then  b", "Build (home)"],
    ["g  then  r", "Runs"],
    ["g  then  p", "Profiles"],
    ["g  then  t", "Quick Builds (Templates)"],
    ["g  then  e", "Explore"],
    ["g  then  k", "Playbooks"],
    ["g  then  c", "AI Coach"],
    ["g  then  d", "Docs"],
    ["?", "Show command palette help"],
    ["Ctrl + Enter", "Launch (on Configure)"],
    ["Esc", "Close modal / palette"],
  ];
  return (
    <ul style={{ ...UL_STYLE, listStyle: "none", marginLeft: 0 }}>
      {items.map(([k, label]) => (
        <li
          key={k}
          style={{ display: "flex", gap: 12, alignItems: "center" }}
        >
          <span
            style={{
              minWidth: 110,
              display: "inline-flex",
              gap: 4,
              flexWrap: "wrap",
            }}
          >
            {k.split(/\s+/).map((part, i) => (
              <span key={i} className="kbd">
                {part}
              </span>
            ))}
          </span>
          <span>{label}</span>
        </li>
      ))}
    </ul>
  );
}

function CustomPack(): JSX.Element {
  return (
    <ol style={OL_STYLE}>
      <li>On the Configure page, find &quot;Or describe a new company&quot; in the left panel</li>
      <li>Describe your company: industry, products, size, anything relevant</li>
      <li>Click &quot;Generate pack with AI&quot; — Claude creates the full configuration</li>
      <li>Or paste a company website URL and click &quot;From URL&quot; to generate a pack from that site</li>
      <li>You&apos;ll be taken to the Pack Editor to review and refine</li>
      <li>
        Use the AI chat to make changes: &quot;add two more products&quot;,
        &quot;increase deal sizes&quot;
      </li>
      <li>Save your pack, then submit it for team review if you want to share</li>
    </ol>
  );
}

function ImportJson(): JSX.Element {
  return (
    <>
      <p style={P_STYLE}>
        Power users can export a pack as JSON, modify it manually or with
        Claude, and re-import it.
      </p>
      <ol style={OL_STYLE}>
        <li>Open the Pack Editor for any pack and click &quot;Export JSON&quot;</li>
        <li>Modify the JSON in any editor, or paste it into Claude with instructions</li>
        <li>On the Configure page, click &quot;Import JSON&quot; to upload your modified file</li>
        <li>Or paste the JSON directly into the AI instructions panel — it auto-detects JSON</li>
      </ol>
    </>
  );
}

function DemoStories(): JSX.Element {
  return (
    <>
      <p style={P_STYLE}>
        Each story shapes the generated data to reveal specific patterns
        when viewed in Tableau.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 18,
          marginTop: 8,
        }}
      >
        <div>
          <div className="label" style={{ marginBottom: 6 }}>
            Sales Cloud
          </div>
          <ul style={UL_STYLE}>
            <li><Strong>Pipeline Health Crisis</Strong> — Win rates declining, deals stalling</li>
            <li><Strong>Rep Ramp Trajectory</Strong> — New hires ramping at different speeds</li>
            <li><Strong>Product Mix Shift</Strong> — Revenue shifting between product lines</li>
            <li><Strong>Lead Source ROI</Strong> — Channel effectiveness differences</li>
            <li><Strong>Regional Performance Gap</Strong> — Territory imbalances</li>
            <li><Strong>Forecast Accuracy</Strong> — Prediction vs actual by manager</li>
            <li><Strong>Account Turnaround</Strong> — At-risk accounts showing recovery</li>
          </ul>
        </div>
        <div>
          <div className="label" style={{ marginBottom: 6 }}>
            Service Cloud
          </div>
          <ul style={UL_STYLE}>
            <li><Strong>Satisfaction Transformation</Strong> — CSAT improving over time</li>
            <li><Strong>Agent Performance</Strong> — Handle time and quality spread</li>
            <li><Strong>Regional Service Quality</Strong> — Geographic patterns</li>
            <li><Strong>Response Time → Satisfaction</Strong> — Speed impacts CSAT</li>
            <li><Strong>Omni-Channel Optimization</Strong> — Email vs Chat vs Phone</li>
            <li><Strong>Knowledge Base ROI</Strong> — KB adoption reducing resolution time</li>
            <li><Strong>Product Support Excellence</Strong> — Case patterns by product</li>
            <li><Strong>SLA Compliance Journey</Strong> — Tier-based SLA performance</li>
            <li><Strong>Premium Support ROI</Strong> — Gold/Silver/Bronze value</li>
          </ul>
        </div>
      </div>
    </>
  );
}

function DataCloud(): JSX.Element {
  return (
    <>
      <p style={P_STYLE}>
        Found under <Strong>Advanced</Strong> on the Configure page. Requires
        a one-time manual setup in your org — Salesforce blocks automated
        browser setup from cloud IPs, so deep links guide you through it.
      </p>
      <ol style={OL_STYLE}>
        <li>
          Click <Strong>Open Data 360 Setup</Strong> → click &quot;Get Started&quot;
          → wait ~5 minutes for the 4 automated steps
        </li>
        <li>
          Click <Strong>Open Tableau Next Setup</Strong> → flip the Enable
          toggle
        </li>
        <li>
          Click <Strong>Open Tableau Next Features</Strong> → enable all
          feature toggles
        </li>
      </ol>
      <p style={{ ...P_STYLE, marginTop: 10 }}>
        After setup, re-run the pipeline (or launch with DC-only mode) to
        deploy analytics templates, data streams, and the C360 semantic model.
      </p>
    </>
  );
}

function AfterBuild(): JSX.Element {
  return (
    <ul style={UL_STYLE}>
      <li><Strong>Open Org</Strong> — During and after the pipeline, the &quot;Open org in new tab ↗&quot; link lets you watch records appear in real-time</li>
      <li><Strong>Playbook</Strong> — Demo scripts organized by story archetype with suggested dialogue</li>
      <li><Strong>Explorer</Strong> — Query and analyze the generated data with natural language prompts</li>
      <li><Strong>History</Strong> — View past runs, see phase timings, and re-run with the same config</li>
      <li><Strong>Refresh demo dates</Strong> — Shifts all dates forward so &quot;last 30 days&quot; always has data</li>
      <li><Strong>Data summary CSV</Strong> — Download a record count breakdown</li>
    </ul>
  );
}

function Features(): JSX.Element {
  return (
    <ul style={UL_STYLE}>
      <li>Industry packs — Cirrus Renewable Energy, Northern Trail Outfitters, or fork your own</li>
      <li>Pack from URL — Paste a company website URL to generate a pack with AI</li>
      <li>Pack compare — Side-by-side comparison of two packs</li>
      <li>Community reviews — Read and write short reviews on approved packs</li>
      <li>Volume presets — Tiny to X-Large (cases, opps, leads)</li>
      <li>Demo stories — Pipeline health, satisfaction, knowledge base ROI, agent performance</li>
      <li>AI instructions — Describe how you want data to behave</li>
      <li>Pack Editor — Edit products, refine with AI chat, export/import JSON</li>
      <li>Pack detail — Click pack name for full description, products, reviews, Fork/Submit</li>
      <li>Pipeline timing — Phase-by-phase breakdown on completion</li>
      <li>CSV export — Download org data summary after provisioning</li>
    </ul>
  );
}

function Reviews(): JSX.Element {
  return (
    <p style={P_STYLE}>
      Approved, featured, and built-in packs support written reviews. Open a
      pack&apos;s detail modal to read reviews and add your own (1–2
      sentences). Reviews require an org connection. The &quot;Used in X
      demos&quot; badge shows how many times the pack has been provisioned.
    </p>
  );
}

function Webhooks(): JSX.Element {
  return (
    <>
      <p style={P_STYLE}>
        Admins can register webhooks to receive events when jobs complete,
        jobs fail, or packs are approved. Events:{" "}
        <Code>job.completed</Code>, <Code>job.failed</Code>,{" "}
        <Code>pack.approved</Code>. Payloads include job_id, pack_name,
        user_email, and event-specific fields. Optional HMAC signing via{" "}
        <Code>X-QBranch-Signature</Code> header.
      </p>
      <ul style={UL_STYLE}>
        <li><Code>GET /api/webhooks</Code> — List webhooks</li>
        <li><Code>POST /api/webhooks</Code> — Create webhook</li>
        <li><Code>GET /api/webhooks/events</Code> — List event types</li>
      </ul>
    </>
  );
}

function Curator(): JSX.Element {
  return (
    <p style={P_STYLE}>
      Curators can approve/reject community packs and feature top packs. Log
      in with a curator email to access the review queue and library
      analytics.
    </p>
  );
}

function HowItWorks(): JSX.Element {
  return (
    <p style={P_STYLE}>
      The generator connects to your Salesforce org via OAuth, installs a
      metadata package (custom objects, permission sets, roles), then runs a
      parallel pipeline of 40+ Python scripts that create archetype-driven
      demo data across Sales Cloud and Service Cloud. Each account,
      opportunity, case, and agent behavior is shaped by archetypes (Growth
      Engine, Steady Partner, At-Risk, etc.) so the data tells realistic,
      correlated stories when viewed in Tableau. The pipeline uses the
      Salesforce Bulk API v2 for high-throughput uploads and runs on Heroku
      with Redis for job queuing and WebSocket progress streaming.
    </p>
  );
}

function Volumes(): JSX.Element {
  return (
    <>
      <p style={P_STYLE}>Record counts by preset.</p>
      <TableShell
        headers={["Preset", "Accounts", "Opps", "Cases", "Leads", "~Duration"]}
        rows={[
          ["Tiny", "50", "300", "1,000", "200", "5–7 min"],
          ["Small", "75", "500", "3,000", "300", "8–10 min"],
          ["Medium", "200", "3,000", "20,000", "1,500", "15–20 min"],
          ["Large", "300", "5,000", "30,000", "2,000", "20–30 min"],
          ["X-Large", "500", "10,000", "50,000", "5,000", "30–45 min"],
        ]}
      />
      <p style={{ ...P_STYLE, fontSize: 11, color: "var(--ink-3)", marginTop: 8 }}>
        Tiny and Small skip forecasting and historical trend generation for
        speed. Duration depends on Salesforce API response times.
      </p>
    </>
  );
}

function Explorer(): JSX.Element {
  return (
    <>
      <p style={P_STYLE}>
        Natural language queries in the Explore tab. Example prompts:
      </p>
      <ul style={UL_STYLE}>
        <li>&quot;Show me win rate by month&quot;</li>
        <li>&quot;Top 10 accounts by opportunity amount&quot;</li>
        <li>&quot;Cases by priority and status&quot;</li>
        <li>&quot;Average deal size by lead source&quot;</li>
        <li>&quot;Open opportunities closing this quarter&quot;</li>
        <li>&quot;Agent handle time vs CSAT&quot;</li>
      </ul>
    </>
  );
}

function Troubleshooting(): JSX.Element {
  const items: Array<[string, string]> = [
    [
      "Job fails at install_demo_package",
      "Package not installed or wrong version ID. Check DEMO_METADATA_PACKAGE_VERSION_ID in Heroku config.",
    ],
    [
      "Bulk insert failed: ALL records on generate_opportunities",
      "Field-level security — connected user can't write custom fields. Assign GDO_Access permission set to the connected user.",
    ],
    [
      "Session expired after starting a job",
      "Salesforce access token expired (2hr lifetime). Disconnect and reconnect your org.",
    ],
    [
      "Job stalls — no progress for 5+ min",
      "Heroku worker dyno restarted. Cancel and start a new job.",
    ],
    [
      "Custom pack fails but Cirrus works",
      "Pack JSON missing required sections. Open in Pack Editor, check for empty products / team / account_naming.",
    ],
    [
      "Dashboards show no data after provisioning",
      "Date range filter — data is backdated. Use 'Refresh demo dates' on the completion screen.",
    ],
  ];
  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}
    >
      {items.map(([title, body]) => (
        <div
          key={title}
          style={{
            background: "var(--cb-95)",
            border: "1px solid var(--bd)",
            borderRadius: 8,
            padding: "10px 12px",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>
            {title}
          </div>
          <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 3, lineHeight: 1.5 }}>
            {body}
          </div>
        </div>
      ))}
    </div>
  );
}

function ApiReference(): JSX.Element {
  const rows: string[] = [
    "POST /api/jobs/provision — Start a provisioning job",
    "GET /api/jobs/{id} — Get job status and progress",
    "GET /api/packs — List available industry packs",
    "GET /api/packs/compare — Compare two packs (pack_a, pack_b)",
    "GET /api/packs/{id}/reviews — List pack reviews",
    "POST /api/ai/generate-pack — AI pack generation",
    "POST /api/ai/generate-pack-from-url — Generate pack from company URL",
    "POST /api/ai/demo-coach — Personalized demo plan",
    "POST /api/explorer/query — Natural language data query",
    "GET /api/org/status — Org connection status",
    "GET /api/webhooks — List webhooks (admin)",
    "GET /health — Health check (no auth required)",
  ];
  return (
    <>
      <ul style={{ ...UL_STYLE, listStyle: "none", marginLeft: 0 }}>
        {rows.map((r) => {
          const [path, ...rest] = r.split(" — ");
          return (
            <li
              key={path}
              style={{ display: "flex", gap: 10, alignItems: "baseline" }}
            >
              <Code>{path}</Code>
              <span>{rest.join(" — ")}</span>
            </li>
          );
        })}
      </ul>
      <p style={{ ...P_STYLE, fontSize: 11, color: "var(--ink-3)", marginTop: 10 }}>
        Full interactive API docs:{" "}
        <a
          href="/api-redoc"
          target="_blank"
          rel="noreferrer"
          style={{ color: "var(--eb-50)" }}
        >
          /api-redoc
        </a>
      </p>
    </>
  );
}

function NeedHelp(): JSX.Element {
  return (
    <div
      className="card"
      style={{
        padding: "16px 20px",
        background: "linear-gradient(135deg, #f8f5fe, white 70%)",
        border: "1px solid #e9dcfb",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 14,
      }}
    >
      <div>
        <div className="label" style={{ color: "var(--violet)" }}>
          Stuck?
        </div>
        <div style={{ fontSize: 13, color: "var(--ink)", marginTop: 4 }}>
          Reach the Q Branch team in <Strong>#tableau-q-branch</Strong> on
          Slack, or jump straight to a working preset.
        </div>
      </div>
      <Link
        to="/templates"
        className="btn primary"
        style={{ textDecoration: "none", whiteSpace: "nowrap" }}
      >
        Open Quick Builds →
      </Link>
    </div>
  );
}
